import React, { useRef, useState } from 'react'
import { Theme } from '@mui/material'
import {
  GridOnOutlined,
  TextFieldsOutlined,
  FunctionsOutlined,
  WidgetsOutlined,
  AssignmentOutlined,
} from '@mui/icons-material'
import {
  IReportComponentInstance,
  ITableComponentInstance,
  ITextComponentInstance,
  ITopBarComponentInstance,
  IFunctionComponentInstance,
  IInstructionComponentInstance,
  ReportComponentType,
} from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { CanvasTextItem } from './CanvasTextItem'
import { CanvasTopBarItem } from './CanvasTopBarItem'
import { CanvasTableItem } from './CanvasTableItem'
import { CanvasFunctionItem } from './CanvasFunctionItem'
import { CanvasInstructionItem } from './CanvasInstructionItem'
import { CanvasDefaultItem } from './CanvasDefaultItem'

export const COMPONENT_ICON: Record<ReportComponentType, React.ReactNode> = {
  [ReportComponentType.TopBar]: <WidgetsOutlined fontSize="small" />,
  [ReportComponentType.DataTable]: <GridOnOutlined fontSize="small" />,
  [ReportComponentType.Text]: <TextFieldsOutlined fontSize="small" />,
  [ReportComponentType.Function]: <FunctionsOutlined fontSize="small" />,
  [ReportComponentType.Instruction]: <AssignmentOutlined fontSize="small" />,
}

export const getComponentAccent = (theme: Theme): Record<ReportComponentType, string> => ({
  [ReportComponentType.TopBar]: theme.palette.primaryDark.main,
  [ReportComponentType.DataTable]: theme.palette.info.main,
  [ReportComponentType.Text]: theme.palette.infoMain.main,
  [ReportComponentType.Function]: theme.palette.warningMain.main,
  [ReportComponentType.Instruction]: theme.palette.success.main,
})

export interface IUseDesignerCanvasActionsProps {
  canvasItems: IReportComponentInstance[]
  onReorder?: (fromIndex: number, toIndex: number) => void
  onDropAtIndex?: (e: React.DragEvent, insertIndex: number) => void
  onDrop?: (e: React.DragEvent) => void
  onDragOver?: (e: React.DragEvent) => void
}

export const useDesignerCanvasActions = ({
  canvasItems,
  onReorder,
  onDropAtIndex,
  onDrop,
  onDragOver,
}: IUseDesignerCanvasActionsProps) => {
  const dragItemIndex = useRef<number | null>(null)
  const [insertIndex, setInsertIndex] = useState<number | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  const performDrop = (e: React.DragEvent, overrideInsertIndex?: number) => {
    const canvasItemId = e.dataTransfer.getData('canvasItemId')
    const currentInsertIndex = overrideInsertIndex ?? insertIndex ?? canvasItems.length

    if (canvasItemId && dragItemIndex.current !== null) {
      const fromIndex = dragItemIndex.current
      const toIndex = currentInsertIndex > fromIndex ? currentInsertIndex - 1 : currentInsertIndex
      if (toIndex !== fromIndex) {
        onReorder?.(fromIndex, toIndex)
      }
    } else if (!canvasItemId) {
      if (onDropAtIndex) {
        onDropAtIndex(e, currentInsertIndex)
      } else {
        onDrop?.(e)
      }
    }

    dragItemIndex.current = null
    setInsertIndex(null)
    setDraggingIndex(null)
    setIsDragActive(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(true)
    onDragOver?.(e)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as Node | null
    if (!e.currentTarget.contains(relatedTarget)) {
      setInsertIndex(null)
      setIsDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    performDrop(e)
  }

  const handleItemDragStart = (e: React.DragEvent, index: number) => {
    dragItemIndex.current = index
    setDraggingIndex(index)
    setIsDragActive(true)
    e.dataTransfer.setData('canvasItemId', canvasItems[index].id)
    e.dataTransfer.effectAllowed = 'move'

    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const clone = el.cloneNode(true) as HTMLElement
    clone.style.position = 'fixed'
    clone.style.top = `${rect.top}px`
    clone.style.left = `${rect.left}px`
    clone.style.width = `${rect.width}px`
    clone.style.height = `${rect.height}px`
    clone.style.margin = '0'
    clone.style.pointerEvents = 'none'
    clone.style.zIndex = '9999'
    clone.style.opacity = '1'
    clone.style.boxShadow = '0 4px 16px rgba(0,0,0,0.22)'
    document.body.appendChild(clone)
    e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top)
    requestAnimationFrame(() => document.body.removeChild(clone))
  }

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const newInsertIndex = e.clientY < midY ? index : index + 1
    setInsertIndex(newInsertIndex)
  }

  const handleItemDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    performDrop(e)
  }

  const handleItemDragEnd = () => {
    dragItemIndex.current = null
    setInsertIndex(null)
    setDraggingIndex(null)
    setIsDragActive(false)
  }

  const handleBottomZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setInsertIndex(canvasItems.length)
  }

  const handleBottomZoneDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    performDrop(e, canvasItems.length)
  }

  return {
    insertIndex,
    draggingIndex,
    isDragActive,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleItemDragStart,
    handleItemDragOver,
    handleItemDrop,
    handleItemDragEnd,
    handleBottomZoneDragOver,
    handleBottomZoneDrop,
  }
}

export interface IRenderCanvasItemContext {
  componentAccent: Record<ReportComponentType, string>
  selectedItemId?: string | null
  draggingIndex: number | null
  columns?: IFormSchemaColumn[]
  onSelectItem?: (id: string) => void
  onDeleteItem?: (id: string) => void
  handleItemDragStart: (e: React.DragEvent, index: number) => void
  handleItemDragOver: (e: React.DragEvent, index: number) => void
  handleItemDrop: (e: React.DragEvent) => void
  handleItemDragEnd: () => void
}

export const renderCanvasItem = (
  item: IReportComponentInstance,
  index: number,
  ctx: IRenderCanvasItemContext,
): React.ReactNode => {
  const accent = ctx.componentAccent[item.type]
  const isSelected = item.id === ctx.selectedItemId
  const isDragging = ctx.draggingIndex === index
  const isDragOver = false

  const commonDragProps = {
    onDragStart: (e: React.DragEvent) => ctx.handleItemDragStart(e, index),
    onDragOver: (e: React.DragEvent) => ctx.handleItemDragOver(e, index),
    onDrop: ctx.handleItemDrop,
    onDragEnd: ctx.handleItemDragEnd,
  }

  switch (item.type) {
    case ReportComponentType.Text: {
      const textItem = item as ITextComponentInstance
      return (
        <CanvasTextItem
          key={item.id}
          item={textItem}
          isSelected={isSelected}
          accentColor={accent}
          isDragging={isDragging}
          isDragOver={isDragOver}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
          {...commonDragProps}
        />
      )
    }

    case ReportComponentType.TopBar: {
      const topBarItem = item as ITopBarComponentInstance
      return (
        <CanvasTopBarItem
          key={item.id}
          item={topBarItem}
          isSelected={isSelected}
          accentColor={accent}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
        />
      )
    }

    case ReportComponentType.DataTable: {
      const tableItem = item as ITableComponentInstance
      return (
        <CanvasTableItem
          key={item.id}
          item={tableItem}
          componentIcon={COMPONENT_ICON[item.type]}
          isSelected={isSelected}
          accentColor={accent}
          isDragging={isDragging}
          isDragOver={isDragOver}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
          {...commonDragProps}
        />
      )
    }

    case ReportComponentType.Function: {
      const functionItem = item as IFunctionComponentInstance
      return (
        <CanvasFunctionItem
          key={item.id}
          item={functionItem}
          componentIcon={COMPONENT_ICON[item.type]}
          isSelected={isSelected}
          accentColor={accent}
          isDragging={isDragging}
          isDragOver={isDragOver}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
          {...commonDragProps}
        />
      )
    }

    case ReportComponentType.Instruction: {
      const instructionItem = item as IInstructionComponentInstance
      return (
        <CanvasInstructionItem
          key={item.id}
          item={instructionItem}
          componentIcon={COMPONENT_ICON[item.type]}
          isSelected={isSelected}
          accentColor={accent}
          isDragging={isDragging}
          isDragOver={isDragOver}
          columns={ctx.columns}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
          {...commonDragProps}
        />
      )
    }

    default: {
      return (
        <CanvasDefaultItem
          key={(item as any).id}
          item={item as any}
          componentIcon={COMPONENT_ICON[(item as any).type]}
          isSelected={isSelected}
          accentColor={accent}
          isDragging={isDragging}
          isDragOver={isDragOver}
          onSelectItem={ctx.onSelectItem || (() => {})}
          onDeleteItem={ctx.onDeleteItem || (() => {})}
          {...commonDragProps}
        />
      )
    }
  }
}
